import { Trans } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { type ReactNode, useEffect, useState } from "react";

import { ApiConnector } from "../../../api/api-connector";
import { CellLastEdit } from "../../../components/ui-kit/tables/cells/cell-last-edit";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import { SelectionMode } from "../../../models/data-model";
import { Patient } from "../patient";
import { Symptoms, SymptomsItem } from "./symptoms";

export const SymptomsTable = observer(function SymptomsTable(props: {
  data: SymptomsItem[];
  patient: Patient;
  api: ApiConnector;
  onInvalidated?: () => void;
  disableEdit?: boolean;
  disableHistory?: boolean;
  disablePagination?: boolean;
  persistState?: boolean;
  canSelect?: SelectionMode;
  onSelect?: (selected: string[]) => void;
  preselected?: string[];
  filterActions?: ReactNode;
}) {
  const [model] = useState(() => {
    const m = new Symptoms(
      [],
      props.api.permissions,
      props.api.requireUserUUID(),
    );
    if (props.disableHistory) {
      m.hasHistory = false;
    }
    return m;
  });

  useEffect(() => {
    model.updateData(props.data);
  }, [props.data, model]);

  return (
    <DataTable
      filterActions={props.filterActions}
      persistState={props.persistState}
      editMode="dialog"
      model={model}
      itemKeyField="uuid"
      columns={[
        {
          header: <Trans>Symptom datetime</Trans>,
          attr: "symptom_datetime",
          type: "datetime",
          editable: true,
          sort: true,
          initSort: "desc",
        },
        {
          header: <Trans>Symptom</Trans>,
          attr: "symptom_name",
          type: "string",
          sort: true,
        },
        {
          header: <Trans>Value</Trans>,
          attr: "symptom_value",
          type: "number",
          precision: 0,
          editable: true,
        },
        {
          header: <Trans>Change in last 6 months</Trans>,
          attr: "symptom_change_in_last_six_m",
          type: "boolean",
          editable: true,
        },
        {
          header: <Trans>Change date</Trans>,
          attr: "symptom_change_date",
          type: "date",
          editable: true,
        },
        {
          header: <Trans>Value before</Trans>,
          attr: "symptom_value_before",
          type: "number",
          precision: 0,
          editable: true,
        },
        {
          header: <Trans>Comment</Trans>,
          attr: "comment",
          type: "string",
          multiline: true,
          editable: true,
        },
        model.showingHistory && {
          header: <Trans>Added</Trans>,
          attr: "created_at",
          type: "datetime",
        },
        model.showingHistory && {
          header: <Trans>Last edit</Trans>,
          cell: ({ item }) => <CellLastEdit item={item} />,
        },
      ]}
      onEdit={
        props.disableEdit
          ? undefined
          : async (editedField, item) => {
              await props.patient.updateItem(
                "symptoms",
                item.uuid,
                editedField,
              );
              props.onInvalidated?.();
            }
      }
      onDelete={
        props.disableEdit
          ? undefined
          : async (item) => {
              await props.patient.deleteItem("symptoms", item.uuid);
              props.onInvalidated?.();
            }
      }
      rowsPerPage={props.disablePagination ? Number.MAX_VALUE : 25}
      canSelect={props.canSelect}
      onSelect={props.onSelect}
      preselected={props.preselected}
    />
  );
});
